<script lang="ts">
	import type { InputSelectOption, InputSelectProps } from '../types';
	import Input from '../Input.svelte';
	import { clickOutside } from '$lib/actions';
	import { attrValue } from '$lib/utils/html';
	import { fade } from 'svelte/transition';
	import { t } from 'svelte-i18n';
	import Icon from '../../Icon.svelte';
	import { capitalize } from '$lib/utils/strings';
	import InputText from './InputText.svelte';
	import DynamicComponent from '$lib/components/DynamicComponent.svelte';

	export let props: InputSelectProps;

	export let isDropdownOpen = false;
	let searchValue = '';

	let searchInputProps = { placeholder: $t('forms.search'), classNames: 'border-grey' };

	$: searchValue = searchInputProps?.value ? searchInputProps?.value : '';
	$: disabled = props && props.disabled;
	$: ddOpenClass = isDropdownOpen ? 'open' : '';
	$: searchedData = (p: InputSelectOption[]) =>
		p.filter(
			(d) => Array.isArray(d.value) || d.label.toLowerCase().includes(searchValue.toLowerCase())
		);
	$: optionsList =
		props && props.withSearchBar && props?.options ? searchedData(props?.options) : props?.options;
	$: selectedClass = (o: InputSelectOption) =>
		props.value &&
		(props.value === o.value ||
			(Array.isArray(props.value) && props.value.findIndex((e) => e === o.value) !== -1))
			? 'selected'
			: '';
	$: disabledClass = (o: InputSelectOption) => (o.disabled ? 'disabled' : '');
	$: getSelectedLabel = (): string | never => {
		let label = props.placeholder ? props.placeholder : $t('forms.choose');
		if (props?.options) {
			props.options.forEach((e) => {
				if (e.value === props.value) {
					label = e.label;
				} else if (Array.isArray(e.value)) {
					e.value.forEach((i) => {
						if (i.value === props.value) {
							label = i.label;
						}
					});
				}
			});
		}
		return label;
	};

	const getLabelByValue = (v: string) => {
		const opt = props.options?.find((e) => e.value === v);
		return opt?.label ?? v;
	};

	const resetLocalState = () => {
		isDropdownOpen = false;
		searchValue = '';
	};

	const toggleDropdown = () => {
		if (!disabled) {
			isDropdownOpen = !isDropdownOpen;
			searchValue = '';
			// if (isDropdownOpen && !props.touched) {
			//   props.touched = true;
			// }
		}
	};

	const unselectItem = (v: any) => {
		if (Array.isArray(props.value)) {
			props.value = props.value.filter((e) => e !== v);
		}
	};

	const selectItem = (i: InputSelectOption) => {
		if (!i.disabled) {
			if (!props.multiple) {
				props.value = i.value;
				isDropdownOpen = false;
			} else if (Array.isArray(props.value)) {
				let index = props.value.findIndex((e) => e === i.value);
				if (index !== -1) {
					unselectItem(i.value);
				} else {
					props.value = [...props.value, i.value];
				}
			}
			if (!props.dirty) {
				props.dirty = true;
			}
		}
		props = props;
	};
</script>

<Input bind:props>
	<div
		use:clickOutside={{ action: resetLocalState }}
		class={attrValue(['select-container cursor-pointer', ddOpenClass, props?.classNames || ''])}
		{disabled}
	>
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<!-- svelte-ignore a11y-no-static-element-interactions -->
		<div class="select select__trigger" on:click={toggleDropdown}>
			{#if props.multiple}
				<div class="flex flex-wrap space-x-1 mr-4">
					{#if props.value && props.value.length}
						{#each props.value as v}
							<div
								class="selected-item"
								on:click|stopPropagation={() => !props.disabled && unselectItem(v)}
								in:fade|global={{ duration: 200 }}
								out:fade|global={{ duration: 200 }}
								class:disabled={props.disabled}
							>
								<span class="selected-item__label">{capitalize(getLabelByValue(v))}</span>
								<Icon size="small" name="close" />
							</div>
						{/each}
					{:else}
						<span class="mr-4"
							>{props.placeholder ? props.placeholder : $t('forms.chooseMultiple')}</span
						>
					{/if}
				</div>
			{:else}
				{@const selectedLabel = getSelectedLabel()}
				<span class="mr-4 first-letter:uppercase">
					{#if typeof selectedLabel == 'string'}
						{selectedLabel}
					{:else if typeof selectedLabel == 'boolean'}
						<div class="flex space-x-2">
							<Icon
								name={selectedLabel ? 'check_circle' : 'close_circle'}
								classNames={selectedLabel ? 'text-success' : 'text-error'}
							/>
							<span>{selectedLabel ? 'Valide / complété' : 'Invalide / incomplet'}</span>
						</div>
					{:else}
						<div class="flex items-center gap-2">
							<DynamicComponent component={selectedLabel} />
							{#if selectedLabel.props?.props?.tooltip?.title}
								<span>{selectedLabel.props?.props?.tooltip?.title}</span>
							{/if}
						</div>
					{/if}
				</span>
			{/if}
			<Icon
				name="arrow_down_square"
				classNames="transform text-secondary transition {!isDropdownOpen ? '-rotate-90' : ''}"
				size="small"
			/>
		</div>
		<div class="options">
			{#if props.withSearchBar}
				<div class="options__actions">
					<InputText bind:props={searchInputProps} />
				</div>
			{/if}
			{#if optionsList}
				{#each optionsList as d}
					{#if Array.isArray(d.value)}
						<span class="category">{d.label}</span>
						{#each props.withSearchBar ? searchedData(d.value) : d.value as e}
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<!-- svelte-ignore a11y-no-static-element-interactions -->
							<span
								class={attrValue([
									'option',
									'pl-5 pr-3',
									selectedClass(e),
									disabledClass(e),
									disabledClass(d)
								])}
								class:disabled={props.disabled}
								on:click={() => {
									!props.disabled && !d.disabled && selectItem(e);
								}}
							>
								{capitalize(e.label)}
							</span>
						{/each}
					{:else}
						<!-- svelte-ignore a11y-click-events-have-key-events -->
						<!-- svelte-ignore a11y-no-static-element-interactions -->
						<span
							class={attrValue([
								'option first-letter:uppercase',
								'px-3',
								selectedClass(d),
								disabledClass(d)
							])}
							on:click={() => {
								selectItem(d);
							}}
							on:click
						>
							{#if typeof d.label == 'string'}
								{d.label}
							{:else if typeof d.label == 'boolean'}
								<div class="flex space-x-2">
									<Icon
										name={d.label ? 'check_circle' : 'close_circle'}
										classNames={d.label ? 'text-success' : 'text-error'}
									/>
									<span>{d.label ? 'Valide / complété' : 'Invalide / incomplet'}</span>
								</div>
							{:else}
								<div class="flex items-center gap-2">
									<DynamicComponent component={d.label} />
									{#if d.label?.props?.props?.tooltip?.title}
										<span>{d.label?.props?.props?.tooltip?.title}</span>
									{/if}
								</div>
							{/if}
						</span>
					{/if}
				{/each}
			{/if}
		</div>
	</div>
	{#if props.multiple}
		<select {disabled} id={props.id} bind:value={props.value} multiple />
	{:else}
		<select {disabled} id={props.id} bind:value={props.value} />
	{/if}
</Input>

<style lang="postcss">
	select {
		display: none;
	}

	.select-container {
		@apply relative flex flex-col overflow-hidden select-none w-full;
		&.open {
			@apply overflow-visible;
		}
	}

	.select-container[disabled='true'] .select__trigger {
		@apply cursor-not-allowed opacity-50;
	}

	.select-container[disabled='false'] .select__trigger {
		@apply cursor-pointer;
	}

	.select__trigger {
		@apply transition bg-white placeholder-grey-lighter border-grey-light;
		@apply relative flex items-center justify-between px-3 py-2 leading-normal;
	}

	.select-container.open .select__trigger {
		@apply rounded-bl-none rounded-br-none border-secondary;
	}

	.selected-items {
		@apply flex;
	}

	.selected-item {
		@apply flex items-center px-2 py-1 text-xs leading-3 m-1;
		@apply border border-grey-light rounded-full transition duration-500 ease-in-out;
	}

	.selected-item:not(.disabled) {
		@apply hover:bg-grey-light;
	}

	.selected-item__label {
		@apply mr-1;
	}

	.options {
		@apply border border-secondary rounded-br-lg rounded-bl-lg border-t-0;
		@apply bg-white;
		@apply absolute block top-full left-0 right-0 transition opacity-0 invisible pointer-events-none z-10;
	}

	.options__actions {
		@apply p-3;
	}

	.options__actions :global(input) {
		filter: none;
		@apply transition block w-full bg-white rounded;
		@apply focus:border-primary focus:outline-none focus:ring-0;
	}

	.select-container.open .options {
		@apply opacity-100 visible pointer-events-auto max-h-[300px] overflow-y-auto overflow-x-hidden;
	}

	.category {
		@apply relative block px-3 py-2 leading-tight bg-secondary text-white;
	}

	.option {
		@apply relative block py-2 leading-normal transition;
	}

	.option:not(.disabled) {
		@apply cursor-pointer;
	}

	.option:hover:not(.disabled):not(.selected) {
		@apply bg-grey-lighter;
	}

	.option.selected {
		@apply text-white bg-grey-dark;
	}

	.arrow {
		@apply relative h-3 w-3;
	}

	.arrow::before,
	.arrow::after {
		content: '';
		width: 0.125em;
		@apply absolute h-full transition duration-500;
	}

	.arrow::before {
		left: -5px;
		transform: rotate(45deg);
		background-color: currentColor;
	}

	.arrow::after {
		left: 5px;
		transform: rotate(-45deg);
		background-color: currentColor;
	}

	.open .arrow::before {
		left: -5px;
		transform: rotate(-45deg);
	}

	.open .arrow::after {
		left: 5px;
		transform: rotate(45deg);
	}
</style>
